import React, { useState } from 'react';
import { Text } from '@aag-development/becreatives-ui';
import {
  BxClipboardIcon,
  BxCloseIcon,
  BxLoaderCircleIcon,
  BxShareIcon,
  BxTaskIcon,
} from 'src/components/Boxicons';
import { useToggle } from 'src/helpers/useToggle';
import { Api } from 'src/api';
import { If } from 'src/components/If';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useToast } from 'src/components/ui/use-toast';

type Props = { 
  mediaId: string; 
  requestId?: string; 
  showAiDescription?: boolean; 
} & React.ComponentPropsWithoutRef<'i'>;

const ShareMediaButton: React.FC<Props> = ({ mediaId, requestId, showAiDescription = false, onClick, className, ...props }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useToggle();
  const [isOpen, setOpen, toggle] = useToggle();
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [aiLoading, setAiLoading] = useToggle();
  const [aiModalOpen, setAiModalOpen, toggleAiModal] = useToggle();
  const [aiResult, setAiResult] = useState<{
    status: string;
    description?: string;
    message?: string;
    error_message?: string;
  } | null>(null);

  const generateAIDescription = async () => {
    if (!requestId) {
      toast({
        description: t('Request ID is required for AI analysis'),
        variant: 'destructive',
      });
      return;
    }

    setAiLoading(true);
    setAiResult(null);
    
    try {
      const response = await fetch(`${Api.http.baseUrl}/video-analysis/get-result`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ request_id: requestId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setAiResult(result);
      setAiModalOpen(true);
    } catch (error) {
      console.error('Error getting AI analysis result:', error);
      toast({
        description: t('Failed to get AI analysis result'),
        variant: 'destructive',
      });
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <>
      <If
        when={!loading}
        else={
          <BxLoaderCircleIcon {...props} className={!className ? 'spin' : `${className} spin`} />
        }
      >
        <BxShareIcon
          className={className}
          onClick={async (e) => {
            if (mediaId in urls) {
              setOpen(true);
              return;
            }

            setLoading(true);

            await Api.media
              .share(mediaId)
              .then((url) => {
                setUrls((prev) => ({ ...prev, [mediaId]: url }));
                navigator.clipboard
                  .writeText(url)
                  .then(() =>
                    toast({
                      description: t('Sharing url was copied to clipboard!'),
                      variant: 'info',
                    }),
                  )
                  .catch(() => ({}));
              })
              .then(toggle)
              .then(() => onClick?.(e))
              .catch(() => ({}))
              .finally(() => setLoading(false));
          }}
          {...props}
        />
      </If>
      
      {/* AI Description Button */}
      <If when={showAiDescription}>
        <If
          when={!aiLoading}
          else={
            <BxLoaderCircleIcon className={`${className} spin ms-2`} title="Loading AI Analysis..." />
          }
        >
          <BxTaskIcon
            className={`${className} ms-2`}
            title="AI Description"
            onClick={generateAIDescription}
          />
        </If>
      </If>
      <If when={mediaId in urls}>
        <Modal isOpen={isOpen} toggle={toggle} centered={true}>
          <ModalHeader
            toggle={toggle}
            tag={'div'}
            close={
              <>
                <BxClipboardIcon
                  title={'Copy'}
                  className={'fs-5 c-pointer'}
                  onClick={async () => {
                    await navigator.clipboard.writeText(urls[mediaId]);
                    toggle();
                  }}
                />
                <BxCloseIcon title={'Close'} className={'fs-3 c-pointer ms-3'} onClick={toggle} />
              </>
            }
          >
            <Text tag={'code'}>{urls[mediaId]}</Text>
          </ModalHeader>
        </Modal>
      </If>
      
      {/* AI Description Modal */}
      <If when={aiResult !== null}>
        <Modal isOpen={aiModalOpen} toggle={toggleAiModal} centered={true} size="lg">
          <ModalHeader toggle={toggleAiModal}>
            <Text className="fw-bold">AI Video Analysis</Text>
          </ModalHeader>
          <ModalBody>
            <If when={aiResult?.status === 'completed'}>
              <div className="mb-3">
                <Text className="fw-semibold text-success mb-2">Analysis Complete</Text>
                <Text className="text-muted">{aiResult?.description || 'No description available'}</Text>
              </div>
              <If when={!!aiResult?.description}>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => {
                    navigator.clipboard.writeText(aiResult?.description || '');
                    toast({
                      description: t('AI description copied to clipboard!'),
                      variant: 'info',
                    });
                  }}
                >
                  <BxClipboardIcon className="me-1" />
                  Copy Description
                </button>
              </If>
            </If>
            
            <If when={aiResult?.status === 'processing'}>
              <div className="text-center py-4">
                <BxLoaderCircleIcon className="spin fs-1 text-primary mb-3" />
                <Text className="text-muted">
                  {aiResult?.message || 'Your content is still being analyzed by our AI.'}
                </Text>
              </div>
            </If>
            
            <If when={aiResult?.status === 'pending'}>
              <div className="text-center py-4">
                <BxLoaderCircleIcon className="spin fs-1 text-warning mb-3" />
                <Text className="text-muted">
                  {aiResult?.message || 'Your content is queued for AI analysis.'}
                </Text>
              </div>
            </If>
            
            <If when={aiResult?.status === 'failed'}>
              <div className="text-center py-4">
                <Text className="text-danger fw-semibold mb-2">Analysis Failed</Text>
                <Text className="text-muted">
                  {aiResult?.error_message || 'There was an error analyzing your content.'}
                </Text>
              </div>
            </If>
            
            <If when={aiResult?.status === 'not_found'}>
              <div className="text-center py-4">
                <Text className="text-muted mb-2">No Analysis Available</Text>
                <Text className="text-muted">
                  {aiResult?.message || 'No AI analysis has been performed for this content yet.'}
                </Text>
              </div>
            </If>
          </ModalBody>
        </Modal>
      </If>
    </>
  );
};

export type { Props as ShareMediaButtonProps };
export { ShareMediaButton };
